import React from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import {
  FilterOptions,
  OpportunityType,
  OpportunityStatus,
  GradeLevel,
  InteractionType,
} from "../types";
import { useTheme } from "../contexts/ThemeContext";

interface ActiveFiltersProps {
  filters: FilterOptions;
  onRemoveType: (type: OpportunityType) => void;
  onRemoveStatus: (status: OpportunityStatus) => void;
  onRemoveGradeLevel?: (level: GradeLevel) => void;
  onRemoveInteractionType?: (type: InteractionType) => void;
  onClearAll: () => void;
}

// Color palette for light/dark mode
const colors = {
  light: {
    container: "transparent",
    filterChip: "#F4F4F5",
    filterChipBorder: "#E4E4E7",
    filterText: "#52525B",
    closeIcon: "#52525B",
    clearAllChip: "#E4E4E7",
    clearAllText: "#3F3F46",
  },
  dark: {
    container: "transparent",
    filterChip: "#3F3F46",
    filterChipBorder: "#52525B",
    filterText: "#E4E4E7",
    closeIcon: "#E4E4E7",
    clearAllChip: "#3F3F46",
    clearAllText: "#E4E4E7",
  },
};

const ActiveFilters: React.FC<ActiveFiltersProps> = ({
  filters,
  onRemoveType,
  onRemoveStatus,
  onRemoveGradeLevel,
  onRemoveInteractionType,
  onClearAll,
}) => {
  const { theme } = useTheme();
  const c = colors[theme];

  const hasActiveFilters =
    (filters.type && filters.type.length > 0) ||
    (filters.status && filters.status.length > 0) ||
    (filters.gradeLevel && filters.gradeLevel.length > 0) ||
    (filters.interactionType && filters.interactionType.length > 0);

  if (!hasActiveFilters) return null;

  return (
    <View
      style={[styles.container, { backgroundColor: c.container }]}
      data-oid="v3mgydo"
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        data-oid="ph4h6g9"
      >
        {filters.type?.map((type) => (
          <TouchableOpacity
            key={`type-${type}`}
            style={[
              styles.filterChip,
              {
                backgroundColor: c.filterChip,
                borderColor: c.filterChipBorder,
              },
            ]}
            onPress={() => onRemoveType(type)}
            data-oid="pype3l2"
          >
            <Text
              style={[styles.filterText, { color: c.filterText }]}
              data-oid="iowj8ev"
            >
              {type}
            </Text>
            <Ionicons
              name="close-circle"
              size={16}
              color={c.closeIcon}
              data-oid="ah4dkap"
            />
          </TouchableOpacity>
        ))}

        {filters.status?.map((status) => (
          <TouchableOpacity
            key={`status-${status}`}
            style={[
              styles.filterChip,
              {
                backgroundColor: c.filterChip,
                borderColor: c.filterChipBorder,
              },
            ]}
            onPress={() => onRemoveStatus(status)}
            data-oid="xegbi-4"
          >
            <Text
              style={[styles.filterText, { color: c.filterText }]}
              data-oid="9y-689z"
            >
              {status}
            </Text>
            <Ionicons
              name="close-circle"
              size={16}
              color={c.closeIcon}
              data-oid="c93mmwd"
            />
          </TouchableOpacity>
        ))}

        {filters.gradeLevel?.map((level) => (
          <TouchableOpacity
            key={`grade-${level}`}
            style={[
              styles.filterChip,
              {
                backgroundColor: c.filterChip,
                borderColor: c.filterChipBorder,
              },
            ]}
            onPress={() => onRemoveGradeLevel?.(level)}
            data-oid="6k4fi3r"
          >
            <Text
              style={[styles.filterText, { color: c.filterText }]}
              data-oid="ncs_7j8"
            >
              {level}
            </Text>
            <Ionicons
              name="close-circle"
              size={16}
              color={c.closeIcon}
              data-oid="0-:lsw0"
            />
          </TouchableOpacity>
        ))}

        {filters.interactionType?.map((type) => (
          <TouchableOpacity
            key={`interaction-${type}`}
            style={[
              styles.filterChip,
              {
                backgroundColor: c.filterChip,
                borderColor: c.filterChipBorder,
              },
            ]}
            onPress={() => onRemoveInteractionType?.(type)}
            data-oid="int-filter"
          >
            <Text
              style={[styles.filterText, { color: c.filterText }]}
              data-oid="int-text"
            >
              {type}
            </Text>
            <Ionicons
              name="close-circle"
              size={16}
              color={c.closeIcon}
              data-oid="int-close"
            />
          </TouchableOpacity>
        ))}

        <TouchableOpacity
          style={[
            styles.clearAllChip,
            {
              backgroundColor: c.clearAllChip,
              borderColor: c.filterChipBorder,
            },
          ]}
          onPress={onClearAll}
          data-oid="d3d.3--"
        >
          <Text
            style={[styles.clearAllText, { color: c.clearAllText }]}
            data-oid="yo37yho"
          >
            Clear all
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginTop: 6,
    marginBottom: 2,
    maxWidth: 1200,
    alignSelf: "center",
    width: "100%",
  },
  scrollContent: {
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    minHeight: 40,
  },
  filterChip: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 18,
    paddingVertical: 5,
    paddingHorizontal: 10,
    marginRight: 6,
    borderWidth: 1,
  },
  filterText: {
    fontSize: 13,
    fontWeight: "500",
    marginRight: 4,
    fontFamily: "Inter-Medium",
  },
  clearAllChip: {
    borderRadius: 18,
    paddingVertical: 5,
    paddingHorizontal: 10,
    marginRight: 6,
    borderWidth: 1,
  },
  clearAllText: {
    fontSize: 13,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
  },
});

export default ActiveFilters;
